import { useEffect, useState } from 'react';
import type { AttachmentViewer } from './attachment-kind';
import type { EventAttachment } from './event-attachments';
import { useAttachmentObjectUrl } from './use-attachment-object-url';

interface AttachmentPreviewProps {
  caseId: string;
  attachment: EventAttachment;
  viewer: AttachmentViewer;
  downloading: boolean;
  onDownload: () => void;
}

function isAbortError(error: unknown): boolean {
  return (
    typeof error === 'object' && error !== null && 'name' in error && error.name === 'AbortError'
  );
}

function renderInlinePreview(
  viewer: Exclude<AttachmentViewer, 'none'>,
  attachment: EventAttachment,
  url: string,
  textContent: string | null,
  textError: boolean,
): JSX.Element {
  if (viewer === 'image') {
    return (
      <img
        src={url}
        alt={attachment.fileName ?? 'Bildevedlegg'}
        className="mx-auto h-full max-h-full w-auto max-w-full rounded-md object-contain"
      />
    );
  }
  if (viewer === 'pdf') {
    return (
      <iframe title={attachment.fileName ?? 'PDF'} src={url} className="h-full w-full rounded-md" />
    );
  }
  if (viewer === 'video') {
    return (
      <video
        controls
        src={url}
        className="mx-auto h-full max-h-full w-auto max-w-full rounded-md"
      />
    );
  }
  if (viewer === 'audio') {
    return (
      <div className="rounded-md border border-white/10 bg-black/20 p-3">
        <audio controls src={url} className="w-full" />
      </div>
    );
  }
  if (textError) {
    return <p className="text-sm text-red-300">Kunne ikke lese tekstvedlegget.</p>;
  }
  if (textContent === null) {
    return <p className="text-sm text-text-tertiary">Laster tekstinnhold…</p>;
  }
  return (
    <pre className="h-full max-h-full overflow-auto whitespace-pre-wrap rounded-md border border-white/10 bg-black/20 p-3 text-xs text-text-secondary">
      {textContent}
    </pre>
  );
}

export function AttachmentPreview({
  caseId,
  attachment,
  viewer,
  downloading,
  onDownload,
}: AttachmentPreviewProps) {
  const canLoadInline = viewer !== 'none' && Boolean(attachment.sourceItemId);
  const { objectUrl, isError, isLoading } = useAttachmentObjectUrl({
    caseId,
    sourceItemId: attachment.sourceItemId,
    enabled: canLoadInline,
  });
  const [textContent, setTextContent] = useState<string | null>(null);
  const [textError, setTextError] = useState<boolean>(false);

  useEffect(() => {
    if (viewer !== 'text' || !objectUrl) {
      setTextContent(null);
      setTextError(false);
      return;
    }
    const abortController = new AbortController();
    setTextContent(null);
    setTextError(false);
    void fetch(objectUrl, { signal: abortController.signal })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Kunne ikke lese tekstvedlegg.');
        }
        return response.text();
      })
      .then((content) => setTextContent(content))
      .catch((error: unknown) => {
        if (!isAbortError(error)) {
          setTextError(true);
        }
      });
    return () => {
      abortController.abort();
    };
  }, [viewer, objectUrl]);

  if (!attachment.sourceItemId) {
    return <p className="text-sm text-text-tertiary">Vedlegget kan ikke forhåndsvises.</p>;
  }

  if (viewer === 'none') {
    return (
      <div className="flex flex-col items-start gap-3">
        <p className="text-sm text-text-tertiary">Kan ikke forhåndsvises.</p>
        <button
          type="button"
          onClick={onDownload}
          disabled={downloading}
          className="rounded-md border border-white/15 px-3 py-1.5 text-xs text-text-secondary transition
                     hover:border-white/25 hover:text-text-primary disabled:opacity-60"
        >
          {downloading ? 'Laster ned…' : 'Last ned'}
        </button>
      </div>
    );
  }

  if (isLoading) {
    return <p className="text-sm text-text-tertiary">Laster vedlegg…</p>;
  }

  if (isError || !objectUrl) {
    return <p className="text-sm text-red-300">Kunne ikke åpne forhåndsvisning.</p>;
  }

  return renderInlinePreview(viewer, attachment, objectUrl, textContent, textError);
}
